import axios from 'axios'

export const api = axios.create({
  baseURL: '/api',
  headers: { 'X-Requested-With': 'WhistBuddy' }
})

const AUTH_KEY = 'wb-auth'

/** Pages that don't require a session, so a 401 there shouldn't force a redirect. */
const PUBLIC_PATHS = ['/login', '/auth/callback', '/regler', '/privatliv']

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.includes(pathname) || pathname.startsWith('/j/')
}

/** Lightweight client-side marker: the real session lives in the HttpOnly cookie. */
export function isLoggedIn(): boolean {
  return localStorage.getItem(AUTH_KEY) === '1'
}

export function setAuthMarker(userId: number, name: string): void {
  localStorage.setItem(AUTH_KEY, '1')
  localStorage.setItem('userId', String(userId))
  localStorage.setItem('name', name)
}

export function clearAuthMarker(): void {
  localStorage.removeItem(AUTH_KEY)
  localStorage.removeItem('userId')
  localStorage.removeItem('name')
}

export async function logout(): Promise<void> {
  try {
    await api.post('/auth/logout')
  } finally {
    clearAuthMarker()
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearAuthMarker()
      if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export interface Me {
  id: number
  name: string
  email: string
  createdAt: string
  isAdmin: boolean
  isGuest: boolean
}

export interface GamePlayerSummary {
  userId: number
  name: string
  totalScore: number
  position: number
  isGuest?: boolean
}

export interface GameSummary {
  id: number
  name: string
  status: string
  createdAt: string
  clubId?: number
  clubName?: string
  players: GamePlayerSummary[]
}

export interface GamesResponse {
  userName: string
  games: GameSummary[]
}

export interface AuthConfig {
  googleEnabled: boolean
  passkeysEnabled: boolean
}

export interface RequestCodeResponse {
  message: string
  newUser: boolean
}

export interface VerifyCodeResponse {
  token: string
  userId: number
  email: string
  name: string
}

export interface UpdateMeResponse {
  user: Me
  token?: string
}

export interface CreateGameResponse {
  gameId: number
}

export type ClubRole = 'owner' | 'member'

export interface Club {
  id: number
  name: string
  inviteCode: string
  role: ClubRole
  memberCount: number
}

export interface ClubMember {
  userId: number
  name: string
  isGuest: boolean
  role: ClubRole
  joinedAt: string
}

export interface ClubSeasonEntry {
  userId: number
  name: string
  points: number
  games: number
}

export interface ClubDetails {
  id: number
  name: string
  inviteCode: string
  role: ClubRole
  members: ClubMember[]
  season: ClubSeasonEntry[]
}

export interface CreateClubResponse {
  id: number
  name: string
  inviteCode: string
}

export interface InviteInfo {
  clubName: string
  memberCount: number
}

export interface JoinClubResponse {
  clubId: number
  clubName: string
  joined: true
  guest?: boolean
  token?: string
  userId?: number
  name?: string
}

export interface AttachEmailResponse {
  token: string
  userId: number
  email: string
  name: string
}

export interface InviteEmailResponse {
  ok: true
  alreadyMember?: boolean
}

export interface ClubCandidate {
  userId: number
  name: string
  isGuest: boolean
}

export async function getMe(): Promise<Me> {
  const response = await api.get<Me>('/users/me')
  return response.data
}

export async function getGames(): Promise<GamesResponse> {
  const response = await api.get<GamesResponse>('/games')
  return response.data
}

export async function getAuthConfig(): Promise<AuthConfig> {
  const response = await api.get<AuthConfig>('/auth/config')
  return response.data
}

export async function requestCode(email: string, turnstileToken?: string): Promise<RequestCodeResponse> {
  const response = await api.post<RequestCodeResponse>('/auth/request-code', { email, turnstileToken })
  return response.data
}

export async function verifyCode(email: string, code: string, name?: string): Promise<VerifyCodeResponse> {
  const response = await api.post<VerifyCodeResponse>('/auth/verify-code', { email, code, name })
  return response.data
}

export async function attachEmail(email: string, code: string): Promise<AttachEmailResponse> {
  const response = await api.post<AttachEmailResponse>('/auth/attach-email', { email, code })
  return response.data
}

export async function updateMe(payload: { name?: string; email?: string }): Promise<UpdateMeResponse> {
  const response = await api.patch<UpdateMeResponse>('/users/me', payload)
  return response.data
}

export async function deleteMe(): Promise<void> {
  await api.delete('/users/me')
}

export async function createGame(payload: { name: string; playerIds: number[]; clubId?: number }): Promise<CreateGameResponse> {
  const response = await api.post<CreateGameResponse>('/games', payload)
  return response.data
}

export async function getClubs(): Promise<Club[]> {
  const response = await api.get<Club[]>('/clubs')
  return response.data
}

export async function createClub(name: string): Promise<CreateClubResponse> {
  const response = await api.post<CreateClubResponse>('/clubs', { name })
  return response.data
}

export async function getClub(id: number): Promise<ClubDetails> {
  const response = await api.get<ClubDetails>(`/clubs/${id}`)
  return response.data
}

export async function renameClub(id: number, name: string): Promise<void> {
  await api.patch(`/clubs/${id}`, { name })
}

export async function leaveClub(id: number): Promise<void> {
  await api.post(`/clubs/${id}/leave`)
}

export async function transferClub(id: number, userId: number): Promise<void> {
  await api.post(`/clubs/${id}/transfer`, { userId })
}

export async function deleteClub(id: number): Promise<void> {
  await api.delete(`/clubs/${id}`)
}

export async function removeMember(id: number, userId: number): Promise<void> {
  await api.delete(`/clubs/${id}/members/${userId}`)
}

export async function rotateInvite(id: number): Promise<{ inviteCode: string }> {
  const response = await api.post<{ inviteCode: string }>(`/clubs/${id}/invite/rotate`)
  return response.data
}

export async function inviteByEmail(clubId: number, email: string): Promise<InviteEmailResponse> {
  const response = await api.post<InviteEmailResponse>(`/clubs/${clubId}/invite/email`, { email })
  return response.data
}

export async function getCandidates(clubId: number): Promise<ClubCandidate[]> {
  const response = await api.get<ClubCandidate[]>(`/clubs/${clubId}/candidates`)
  return response.data
}

export async function addMember(clubId: number, userId: number): Promise<void> {
  await api.post(`/clubs/${clubId}/members`, { userId })
}

export async function getInvite(code: string): Promise<InviteInfo> {
  const response = await api.get<InviteInfo>(`/clubs/invite/${code}`)
  return response.data
}

export async function joinClub(code: string, name?: string): Promise<JoinClubResponse> {
  const response = await api.post<JoinClubResponse>('/clubs/join', { code, name })
  return response.data
}

/** Link to share so someone can join the club by scanning or clicking. */
export function inviteUrl(code: string): string {
  return `${window.location.origin}/j/${code}`
}
